import { useEffect, useRef } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, Button, CircularProgress, Link, Stack, Typography, alpha, useTheme } from '@mui/material'
import { HourglassEmptyRounded, HowToRegOutlined, PersonAddAltRounded } from '@mui/icons-material'

import { useSignup } from '../hooks/useSignup'
import usePersist from '../hooks/usePersist'
import useDialogStyles from '../hooks/useDialogStyles'
import { usePathContext } from '../context/path'
import AuthCard, { AuthError, AuthField, OrDivider, PasswordField } from '../components/auth/AuthCard'
import SignInWithGoogleButton from '../components/auth/SignInWithGoogleButton'

const PASSWORD_RULES = ['At least 8 characters', '1 lowercase letter', '1 uppercase letter', '1 number', '1 symbol']

const Signup = () => {
  const theme = useTheme()
  const { goldButtonSx } = useDialogStyles()
  const accent = theme.palette.secondary.main
  const { setTitle } = usePathContext()

  const { signup, error, isLoading, pending } = useSignup()
  const { persist, setPersist } = usePersist()
  const nameRef = useRef(null)
  const emailRef = useRef(null)
  const passwordRef = useRef(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    await signup(
      nameRef.current.value,
      emailRef.current.value.trim(),
      passwordRef.current.value.trim()
    )
  }

  useEffect(() => {
    setTitle("Welcome")
  }, [setTitle])

  const linkSx = { color: accent, fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }

  // ---------- after sign up: check your mail ----------
  if (pending) {
    return (
      <AuthCard icon={HourglassEmptyRounded} title="Registration received" subtitle="Your account is awaiting approval.">
        <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.6, color: 'text.secondary' }}>
          Your account has been created but is not active yet. An administrator needs to activate it before you can log in.
        </Typography>
        <Box
          sx={{
            mt: 2,
            p: 1.25,
            borderRadius: 2,
            fontSize: '0.83rem',
            fontWeight: 600,
            color: accent,
            backgroundColor: alpha(accent, 0.08),
            border: `1px solid ${alpha(accent, 0.3)}`,
          }}
        >
          Please contact your administrator if you need access sooner.
        </Box>
        <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.85rem', color: 'text.secondary' }}>
          Account activated?{' '}
          <Link component={RouterLink} to="/login" sx={linkSx}>Log in</Link>
        </Typography>
      </AuthCard>
    )
  }

  // ---------- sign up form ----------
  return (
    <AuthCard icon={PersonAddAltRounded} title="Create account" subtitle="Sign up to get started.">
      <Box component="form" onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <AuthField label="Username" id="username" inputRef={nameRef} autoComplete="username" autoFocus required />
          <AuthField label="Email address" id="email" type="email" inputRef={emailRef} autoComplete="email" required />
          <PasswordField
            label="Password"
            id="password"
            inputRef={passwordRef}
            autoComplete="new-password"
            helperText="8+ characters with upper and lower case, a number and a symbol"
            FormHelperTextProps={{ sx: { mx: 0.25, color: 'text.secondary' } }}
            required
          />
        </Stack>

        <Button
          type="submit"
          fullWidth
          variant="contained"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <HowToRegOutlined />}
          sx={{ ...goldButtonSx, mt: 2.5, height: 44, fontSize: '0.95rem', textTransform: 'none' }}
        >
          {isLoading ? 'Signing up…' : 'Sign up'}
        </Button>

        {error && (
          <AuthError>
            {error}
            {error === 'Password not strong enough' && (
              <ul>
                {PASSWORD_RULES.map((rule) => <li key={rule}>{rule}</li>)}
              </ul>
            )}
          </AuthError>
        )}
      </Box>

      <OrDivider />
      <SignInWithGoogleButton persist={persist} setPersist={setPersist} label="Sign up with Google" />

      <Typography sx={{ mt: 3, textAlign: 'center', fontSize: '0.85rem', color: 'text.secondary' }}>
        Already have an account?{' '}
        <Link component={RouterLink} to="/login" sx={linkSx}>Log in</Link>
      </Typography>
    </AuthCard>
  )
}

export default Signup
